import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faHeart,
  faHome,
  faMapMarker,
  faShoppingCart,
  faStar,
  faUser,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import {
  getAllByVendorId,
  getAllProductByVendor,
  FaqsItems,
  VendorProducts
} from 'api/products/_requests';
import EcomProfileAddressInfo from 'components/forms/EcomProfileAddressInfo';
import EcomProfilePersonalInfo from 'components/forms/EcomProfilePersonalInfo';
import EcomVendorPersonalInfo from 'components/forms/EcomVendorPersonalInfo';
import EComCustomerOrdersTable from 'components/tables/EComCustomerOrdersTable';
import EcomFaqsVendor from 'components/tables/EcomFaqsVendor';
import EcomProfileReviewsTable from 'components/tables/EcomProfileReviewsTable';
import EcomProfileStoresTable from 'components/tables/EcomProfileStoresTable';
import EcomVendorCouponTable from 'components/tables/EcomVendorCouponTable';
import EcomWishlistTable from 'components/tables/EcomWishlistTable';
import { PF } from 'helpers/utils';
import { useEffect, useMemo, useState } from 'react';
import { Nav, Tab } from 'react-bootstrap';

interface TabLink {
  id: string;
  label: string;
  icon: IconProp;
  number?: number;
  role: 'customer' | 'vendor' | 'both';
  content: (vendorId?: string) => JSX.Element;
}

interface Props {
  role?: 'customer' | 'vendor';
  vendorId?: string;
}

const ProfileDetailsTab: React.FC<Props> = ({ role, vendorId }) => {
  const isVendor = role === 'vendor';

  const { data: vendorFaqs = [] } = useQuery<FaqsItems[]>({
    queryKey: ['vendor-faqs', vendorId],
    queryFn: () => getAllByVendorId(vendorId!),
    enabled: isVendor && !!vendorId
  });

  const [productsData, setProductsData] = useState<VendorProducts[]>([]);
  useEffect(() => {
    if (!isVendor || !vendorId) return;
    (async () => {
      const res = await getAllProductByVendor(vendorId);
      setProductsData(
        res.map(item => ({
          ...item,
          thumbnail: PF + (item.thumbnail ?? 'goods')
        }))
      );
    })();
  }, [isVendor, vendorId]);

  const tabLinks: TabLink[] = useMemo(() => {
    const common: TabLink[] = [
      {
        id: 'orders',
        label: 'Orders',
        icon: faShoppingCart,
        role: 'both',
        content: () => <EComCustomerOrdersTable />
      },
      {
        id: 'review',
        label: 'Reviews',
        icon: faStar,
        number: 24,
        role: 'both',
        content: () => <EcomProfileReviewsTable />
      }
    ];

    const customerOnly: TabLink[] = [
      {
        id: 'wishlist',
        label: 'Wishlist',
        icon: faHeart,
        role: 'customer',
        content: () => <EcomWishlistTable />
      },
      {
        id: 'stores',
        label: 'Stores',
        icon: faHome,
        role: 'customer',
        content: () => <EcomProfileStoresTable />
      },
      {
        id: 'personal-info',
        label: 'Personal info',
        icon: faUser,
        role: 'customer',
        content: () => <EcomProfilePersonalInfo />
      },
      {
        id: 'address',
        label: 'Address',
        icon: faMapMarker,
        role: 'customer',
        content: () => <EcomProfileAddressInfo />
      }
    ];

    const vendorOnly: TabLink[] = isVendor
      ? [
          {
            id: 'customers',
            label: 'Customers',
            icon: faUsers,
            role: 'vendor',
            content: () => <EComCustomerOrdersTable />
          },
          {
            id: 'faqs',
            label: 'FAQS',
            number: vendorFaqs.length,
            icon: faStar,
            role: 'vendor',
            content: vId => (
              <EcomFaqsVendor vendorFaqs={vendorFaqs} vendorId={vId ?? ''} />
            )
          },
          {
            id: 'coupon',
            label: 'Coupons',
            icon: faStar,
            role: 'vendor',
            content: () => <EcomVendorCouponTable vendorId={vendorId} />
          },
          {
            id: 'vendor-personal-info',
            label: 'Other info',
            icon: faUser,
            role: 'vendor',
            content: () => <EcomVendorPersonalInfo />
          }
        ]
      : [];

    return [...common, ...(isVendor ? vendorOnly : customerOnly)];
  }, [isVendor, vendorFaqs]);

  const visibleTabs = tabLinks.filter(
    t => t.role === 'both' || t.role === role
  );

  return (
    <Tab.Container defaultActiveKey="orders">
      <Nav className="mb-3 pb-1 gap-3 nav-underline flex-nowrap scrollbar">
        {visibleTabs.map(t => (
          <Nav.Item key={t.id}>
            <Nav.Link eventKey={t.id} className="text-nowrap">
              <FontAwesomeIcon icon={t.icon} className="me-2" />
              {t.label}
              {t.number ? (
                <span className="text-body-tertiary fw-normal">
                  ({t.number})
                </span>
              ) : null}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>
      <Tab.Content>
        {visibleTabs.map(t => (
          <Tab.Pane key={t.id} eventKey={t.id}>
            {t.content(vendorId)}
          </Tab.Pane>
        ))}
      </Tab.Content>
    </Tab.Container>
  );
};

export default ProfileDetailsTab;
